import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, CalendarPlus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const STATUSES = ["pending", "confirmed", "checked_in", "completed", "cancelled", "no_show"];
const STATUS_COLOR = {
  pending: "bg-muted text-muted-foreground",
  confirmed: "bg-[hsl(var(--info))]/15 text-[hsl(var(--info))]",
  checked_in: "bg-accent/20 text-accent-foreground",
  completed: "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]",
  cancelled: "bg-destructive/15 text-destructive",
  no_show: "bg-destructive/15 text-destructive",
};

export default function AppointmentsPage() {
  const { user } = useAuth();
  const [appts, setAppts] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ duration_min: 30, type: "in_person", status: "pending" });
  const [filter, setFilter] = useState("all");

  const load = async () => {
    const [a, d, p] = await Promise.all([
      api.get("/appointments"),
      api.get("/users?role=doctor"),
      api.get("/patients"),
    ]);
    setAppts(a.data); setDoctors(d.data); setPatients(p.data);
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/appointments", form);
      toast.success("Appointment booked");
      setOpen(false);
      setForm({ duration_min: 30, type: "in_person", status: "pending" });
      load();
    } catch (err) {
      toast.error("Could not book appointment");
    }
  };

  const updateStatus = async (id, status) => {
    await api.put(`/appointments/${id}/status?status=${status}`);
    toast.success(`Status: ${status}`);
    load();
  };

  const filtered = filter === "all" ? appts : appts.filter((a) => a.status === filter);

  return (
    <div className="space-y-5 fade-up" data-testid="appointments-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Appointments</h1>
          <p className="text-sm text-muted-foreground mt-1">{filtered.length} appointments</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="appt-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="gap-2" data-testid="new-appt-btn"><CalendarPlus className="size-4" /> New appointment</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Book appointment</DialogTitle></DialogHeader>
              <form onSubmit={submit} className="space-y-4" data-testid="appt-form">
                <div className="space-y-1.5">
                  <Label>Patient *</Label>
                  <Select value={form.patient_id} onValueChange={(v) => setForm({ ...form, patient_id: v })}>
                    <SelectTrigger data-testid="appt-patient"><SelectValue placeholder="Select patient" /></SelectTrigger>
                    <SelectContent>{patients.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label>Doctor *</Label>
                  <Select value={form.doctor_id} onValueChange={(v) => setForm({ ...form, doctor_id: v })}>
                    <SelectTrigger data-testid="appt-doctor"><SelectValue placeholder="Select doctor" /></SelectTrigger>
                    <SelectContent>{doctors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name} {d.specialty ? `• ${d.specialty}` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Date & time *</Label>
                    <Input type="datetime-local" required value={form.date_raw || ""} onChange={(e) => { const v = e.target.value; setForm({ ...form, date_raw: v, date: v ? new Date(v).toISOString() : "" }); }} data-testid="appt-date" />
                  </div>
                  <div className="space-y-1.5">
                    <Label>Duration (min)</Label>
                    <Input type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: parseInt(e.target.value) || 30 })} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Reason</Label>
                  <Input value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                  <Button type="submit" data-testid="save-appt-btn">Book</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                <th className="py-3 px-5 font-semibold">Date</th>
                <th className="py-3 px-5 font-semibold">Patient</th>
                <th className="py-3 px-5 font-semibold">Doctor</th>
                <th className="py-3 px-5 font-semibold">Reason</th>
                <th className="py-3 px-5 font-semibold">Status</th>
                <th className="py-3 px-5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-muted/40">
                  <td className="py-3 px-5 mono text-xs">{new Date(a.date).toLocaleString()}</td>
                  <td className="py-3 px-5 font-medium">{a.patient_name}</td>
                  <td className="py-3 px-5 text-muted-foreground">{a.doctor_name}</td>
                  <td className="py-3 px-5 text-muted-foreground">{a.reason || "—"}</td>
                  <td className="py-3 px-5">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLOR[a.status] || ""}`}>{a.status}</span>
                  </td>
                  <td className="py-3 px-5 text-right">
                    <Select value={a.status} onValueChange={(v) => updateStatus(a.id, v)}>
                      <SelectTrigger className="w-36 h-8" data-testid={`appt-status-${a.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                    </Select>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan="6" className="py-12 text-center text-muted-foreground">No appointments.</td></tr>}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
